<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

export default {
  components: {
    SettingsSection,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      isTesting: false,
      greetingConfig: {
        enabled: false,
        type: 'list',
        headerText: '',
        bodyText: '',
        footerText: '',
        listButtonText: 'Ver Opções',
        buttons: [
          { id: 'btn_1', title: '' },
          { id: 'btn_2', title: '' },
          { id: 'btn_3', title: '' },
        ],
        listSections: [
          {
            title: '',
            rows: [
              { id: 'opt_1', title: '', description: '' },
              { id: 'opt_2', title: '', description: '' },
            ],
          },
        ],
      },
      testPhone: '',
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    isWhatsAppChannel() {
      return this.inbox.channel_type === 'Channel::Whatsapp';
    },
    greetingTypes() {
      return [
        { value: 'text', label: 'Texto Simples' },
        { value: 'buttons', label: 'Botões (máx. 3)' },
        { value: 'list', label: 'Lista Interativa' },
      ];
    },
  },
  mounted() {
    this.fetchGreetingConfig();
  },
  methods: {
    async fetchGreetingConfig() {
      if (!this.isWhatsAppChannel) return;

      this.isLoading = true;
      try {
        const { data } = await InboxesAPI.getInteractiveGreetingConfig(
          this.inbox.id
        );

        if (data.success && data.config) {
          this.greetingConfig = {
            ...this.greetingConfig,
            ...data.config,
          };
        }
      } catch (error) {
        console.error('Erro ao carregar configuração:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async saveGreetingConfig() {
      this.isSaving = true;
      try {
        const { data } = await InboxesAPI.updateInteractiveGreetingConfig(
          this.inbox.id,
          this.greetingConfig
        );

        if (data.success) {
          useAlert('Configuração salva com sucesso!');
        } else {
          useAlert(data.error || 'Erro ao salvar configuração');
        }
      } catch (error) {
        useAlert('Erro ao salvar: ' + error.message);
      } finally {
        this.isSaving = false;
      }
    },

    async testGreeting() {
      if (!this.testPhone) {
        useAlert('Digite um número de telefone para teste');
        return;
      }

      this.isTesting = true;
      try {
        const { data } = await InboxesAPI.sendTestInteractiveGreeting(
          this.inbox.id,
          this.testPhone
        );

        if (data.success) {
          useAlert('Mensagem de teste enviada com sucesso!');
        } else {
          useAlert(data.reason || data.error || 'Erro ao enviar teste');
        }
      } catch (error) {
        useAlert('Erro ao enviar teste: ' + error.message);
      } finally {
        this.isTesting = false;
      }
    },

    addButton() {
      if (this.greetingConfig.buttons.length < 3) {
        const id = `btn_${this.greetingConfig.buttons.length + 1}`;
        this.greetingConfig.buttons.push({ id, title: '' });
      }
    },

    removeButton(index) {
      this.greetingConfig.buttons.splice(index, 1);
    },

    addSection() {
      const sectionNum = this.greetingConfig.listSections.length + 1;
      this.greetingConfig.listSections.push({
        title: '',
        rows: [{ id: `opt_${sectionNum}_1`, title: '', description: '' }],
      });
    },

    removeSection(index) {
      this.greetingConfig.listSections.splice(index, 1);
    },

    addRow(sectionIndex) {
      const section = this.greetingConfig.listSections[sectionIndex];
      const rowNum = section.rows.length + 1;
      section.rows.push({
        id: `opt_${sectionIndex + 1}_${rowNum}`,
        title: '',
        description: '',
      });
    },

    removeRow(sectionIndex, rowIndex) {
      this.greetingConfig.listSections[sectionIndex].rows.splice(rowIndex, 1);
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      title="Saudação Interativa WhatsApp"
      sub-title="Configure mensagens de boas-vindas automáticas com botões ou listas quando uma nova conversa for criada."
    >
      <div v-if="!isWhatsAppChannel" class="p-4 bg-yellow-50 rounded-lg mb-4">
        <p class="text-yellow-800">
          Esta funcionalidade está disponível apenas para canais WhatsApp Cloud API.
        </p>
      </div>

      <div v-else-if="isLoading" class="p-4 text-center">
        <span>Carregando configuração...</span>
      </div>

      <div v-else class="space-y-6">
        <!-- Habilitar/Desabilitar -->
        <div class="flex items-center gap-3">
          <input
            id="greeting-enabled"
            v-model="greetingConfig.enabled"
            type="checkbox"
            class="w-4 h-4"
          />
          <label for="greeting-enabled" class="font-medium">
            Ativar saudação interativa automática
          </label>
        </div>

        <div v-if="greetingConfig.enabled" class="space-y-6">
          <!-- Tipo de Saudação -->
          <div>
            <label class="block mb-2 font-medium">Tipo de Mensagem</label>
            <select
              v-model="greetingConfig.type"
              class="w-full p-2 border rounded-lg"
            >
              <option
                v-for="type in greetingTypes"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </select>
          </div>

          <!-- Header -->
          <div>
            <label class="block mb-2 font-medium">Título (Header)</label>
            <input
              v-model="greetingConfig.headerText"
              type="text"
              class="w-full p-2 border rounded-lg"
              placeholder="Ex: Menu de Atendimento"
              maxlength="60"
            />
            <p class="text-xs text-slate-500 mt-1">Máximo 60 caracteres</p>
          </div>

          <!-- Body -->
          <div>
            <label class="block mb-2 font-medium">Mensagem Principal (Body)</label>
            <textarea
              v-model="greetingConfig.bodyText"
              class="w-full p-2 border rounded-lg"
              rows="5"
              placeholder="Ex: Olá! Seja bem-vindo ao nosso atendimento..."
              maxlength="1024"
            />
            <p class="text-xs text-slate-500 mt-1">
              Máximo 1024 caracteres. Use *texto* para negrito e _texto_ para itálico.
            </p>
          </div>

          <!-- Footer -->
          <div>
            <label class="block mb-2 font-medium">Rodapé (Footer) - Opcional</label>
            <input
              v-model="greetingConfig.footerText"
              type="text"
              class="w-full p-2 border rounded-lg"
              placeholder="Ex: Responderemos em breve!"
              maxlength="60"
            />
            <p class="text-xs text-slate-500 mt-1">Máximo 60 caracteres</p>
          </div>

          <!-- Configuração de Botões -->
          <div v-if="greetingConfig.type === 'buttons'" class="space-y-4">
            <div class="flex items-center justify-between">
              <label class="font-medium">Botões (máximo 3)</label>
              <NextButton
                v-if="greetingConfig.buttons.length < 3"
                size="small"
                @click="addButton"
              >
                + Adicionar Botão
              </NextButton>
            </div>

            <div
              v-for="(button, index) in greetingConfig.buttons"
              :key="button.id"
              class="flex gap-2 items-center"
            >
              <input
                v-model="button.title"
                type="text"
                class="flex-1 p-2 border rounded-lg"
                :placeholder="`Botão ${index + 1} (máx. 20 caracteres)`"
                maxlength="20"
              />
              <NextButton
                v-if="greetingConfig.buttons.length > 1"
                size="small"
                color-scheme="alert"
                @click="removeButton(index)"
              >
                Remover
              </NextButton>
            </div>
          </div>

          <!-- Configuração de Lista -->
          <div v-if="greetingConfig.type === 'list'" class="space-y-4">
            <div>
              <label class="block mb-2 font-medium">Texto do Botão da Lista</label>
              <input
                v-model="greetingConfig.listButtonText"
                type="text"
                class="w-full p-2 border rounded-lg"
                placeholder="Ex: Ver Opções"
                maxlength="20"
              />
              <p class="text-xs text-slate-500 mt-1">Máximo 20 caracteres</p>
            </div>

            <div class="flex items-center justify-between">
              <label class="font-medium">Seções da Lista</label>
              <NextButton size="small" @click="addSection">
                + Adicionar Seção
              </NextButton>
            </div>

            <div
              v-for="(section, sIndex) in greetingConfig.listSections"
              :key="sIndex"
              class="p-4 border rounded-lg space-y-3 bg-slate-50"
            >
              <div class="flex gap-2 items-center">
                <input
                  v-model="section.title"
                  type="text"
                  class="flex-1 p-2 border rounded-lg"
                  :placeholder="`Título da Seção ${sIndex + 1}`"
                  maxlength="24"
                />
                <NextButton
                  v-if="greetingConfig.listSections.length > 1"
                  size="small"
                  color-scheme="alert"
                  @click="removeSection(sIndex)"
                >
                  Remover Seção
                </NextButton>
              </div>

              <div class="ml-4 space-y-2">
                <div class="flex items-center justify-between">
                  <span class="text-sm text-slate-600">Opções:</span>
                  <NextButton size="small" @click="addRow(sIndex)">
                    + Opção
                  </NextButton>
                </div>

                <div
                  v-for="(row, rIndex) in section.rows"
                  :key="row.id"
                  class="flex gap-2 items-start"
                >
                  <div class="flex-1 space-y-1">
                    <input
                      v-model="row.title"
                      type="text"
                      class="w-full p-2 border rounded-lg text-sm"
                      placeholder="Título (máx. 24 caracteres)"
                      maxlength="24"
                    />
                    <input
                      v-model="row.description"
                      type="text"
                      class="w-full p-2 border rounded-lg text-sm"
                      placeholder="Descrição (máx. 72 caracteres)"
                      maxlength="72"
                    />
                  </div>
                  <NextButton
                    v-if="section.rows.length > 1"
                    size="small"
                    color-scheme="alert"
                    @click="removeRow(sIndex, rIndex)"
                  >
                    X
                  </NextButton>
                </div>
              </div>
            </div>
          </div>

          <!-- Teste -->
          <div class="p-4 border rounded-lg bg-blue-50 space-y-3">
            <label class="font-medium text-blue-800">Testar Saudação</label>
            <div class="flex gap-2">
              <input
                v-model="testPhone"
                type="text"
                class="flex-1 p-2 border rounded-lg"
                placeholder="Número com DDI (ex: 5562999999999)"
              />
              <NextButton
                :is-loading="isTesting"
                @click="testGreeting"
              >
                Enviar Teste
              </NextButton>
            </div>
            <p class="text-xs text-blue-600">
              Envie uma mensagem de teste para verificar como ficará a saudação.
            </p>
          </div>

          <!-- Botão Salvar -->
          <div class="flex justify-end pt-4">
            <NextButton
              :is-loading="isSaving"
              color-scheme="primary"
              @click="saveGreetingConfig"
            >
              Salvar Configuração
            </NextButton>
          </div>
        </div>
      </div>
    </SettingsSection>
  </div>
</template>
